import React, { createContext, useContext, useMemo } from 'react';
import { useAcsChat, type UseAcsChatReturn } from './useAcsChat';
import type { AcsChatConfig } from './AcsChatConfig';

// ============================================================================
// AcsChatProvider – Context for group chat across portals
// ============================================================================
//
// Wrap your page (or app) with this provider so any child component can access
// the chat state via useAcsChatContext().
//
// Example in any portal:
//
//   <AcsChatProvider
//     config={{
//       endpoint: import.meta.env.VITE_ACS_ENDPOINT,
//       token: backendResponse.token,       // from backend
//       userId: backendResponse.userId,      // from backend
//       displayName: 'Dr. Smith',
//     }}
//     threadId={backendResponse.threadId}    // from backend
//   >
//     <Chat
//       messages={...from context}
//       onSendMessage={...from context}
//       ...
//     />
//   </AcsChatProvider>
//
// ============================================================================

interface AcsChatProviderProps {
  /** ACS configuration for the current user */
  config: AcsChatConfig;
  /** Chat thread ID provided by the backend */
  threadId: string;
  /** React children */
  children: React.ReactNode;
}

const AcsChatContext = createContext<UseAcsChatReturn | null>(null);

export const AcsChatProvider: React.FC<AcsChatProviderProps> = ({
  config,
  threadId,
  children,
}) => {
  const chat = useAcsChat({
    config,
    threadId,
    autoConnect: true,
  });

  const value = useMemo(
    () => chat,
    [chat.messages, chat.isConnected, chat.isLoading, chat.error]
  );

  return (
    <AcsChatContext.Provider value={value}>{children}</AcsChatContext.Provider>
  );
};

/**
 * Access the ACS chat state from any child component.
 *
 * Must be used inside an <AcsChatProvider>.
 */
export const useAcsChatContext = (): UseAcsChatReturn => {
  const context = useContext(AcsChatContext);
  if (!context) {
    throw new Error(
      'useAcsChatContext must be used within an <AcsChatProvider>.'
    );
  }
  return context;
};

export default AcsChatProvider;
